import { useCallback, useRef } from "react";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";
import {
  useDesignCommandManager,
  APPLIED_BUT_NOT_CONFIRMED_BY_API_CONSTANT,
} from "@/store/zustand/designCommandManagerStore";
import { AddAndRemoveJsxChange } from "@/store/zustand/changeLedgerTypes";
import { v4 as uuidv4 } from "uuid";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useActiveCanvas, useCanvasId } from "@/utils/utils";
import {
  findElementByDomUniquePath,
  getIframeDocumentForStoryboard,
} from "@/components/Canvas/utils/utils";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { updateCodebaseId } from "@/components/Canvas/Storyboard/StaticIframe/utils/utils";
import { TempoElement } from "@/utils/tempoElement";
import { getComponentFileFromNavTreeNodes } from "@/utils/jsx/extractElementsUtils";
import { toast } from "sonner";
import { useExtractElementStore } from "@/components/Canvas/stores/extractElementStore";
import { processInstantUpdateData } from "@/utils/navTreeUtils";
import { NavTreeNode } from "@/utils/types";

const TABLE_TAGS = new Set([
  "tr",
  "td",
  "th",
  "thead",
  "tbody",
  "tfoot",
  "col",
  "colgroup",
  "caption",
]);

const REORDER_THROTTLE_MS = 200;

type Direction = "up" | "down" | "left" | "right";
type LayoutDirection = "horizontal" | "vertical";

export function useElementReorderShortcuts() {
  const { selectedNavTreeNode, selectedElement } = useSelectedNavTreeNode();
  const executeCommand = useDesignCommandManager(
    (state) => state.executeCommand,
  );
  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const activeCanvas = useActiveCanvas();
  const canvasId = useCanvasId();
  const lastReorderTimeRef = useRef(0);
  const isReorderingRef = useRef(false);
  const layoutCacheRef = useRef<{
    parentCodebaseId: string | null;
    layout: LayoutDirection;
  }>({ parentCodebaseId: null, layout: "vertical" });

  const isInTableContext = useCallback((): boolean => {
    let current: NavTreeNode | null = selectedNavTreeNode || null;
    while (current) {
      const treeElement = treeElementLookup[current.tempoElement?.codebaseId];
      const tag = (
        current.elementTagName ??
        (treeElement?.type === "native"
          ? (treeElement?.componentName ?? "")
          : "")
      ).toLowerCase();
      if (TABLE_TAGS.has(tag) || tag === "table") return true;
      current = current.parent || null;
    }
    return false;
  }, [selectedNavTreeNode, treeElementLookup]);

  const canElementBeReordered = useCallback((): boolean => {
    if (!selectedNavTreeNode || !selectedElement || selectedElement.isEmpty())
      return false;
    if (selectedElement.isStoryboard()) return false;

    const parent = selectedNavTreeNode.parent;
    if (!parent?.children || parent.children.length <= 1) return false;

    const treeElement = treeElementLookup[selectedElement.codebaseId];
    if (!treeElement) return false;

    const classes = treeElement.removableClasses || [];
    if (classes.includes("absolute") || classes.includes("fixed")) return false;

    return !isInTableContext();
  }, [
    selectedNavTreeNode,
    selectedElement,
    treeElementLookup,
    isInTableContext,
  ]);

  const getLayoutFromClasses = useCallback(
    (classList: string[]): LayoutDirection | null => {
      if (
        classList.some(
          (cls) => cls === "flex-row" || cls.startsWith("flex-row-"),
        )
      )
        return "horizontal";
      if (
        classList.some(
          (cls) => cls === "flex-col" || cls.startsWith("flex-col-"),
        )
      )
        return "vertical";
      if (classList.some((cls) => cls.startsWith("grid"))) {
        return classList.some((cls) => cls === "grid-flow-col")
          ? "horizontal"
          : "vertical";
      }
      if (classList.some((cls) => cls === "flex" || cls === "inline-flex"))
        return "horizontal"; // CSS flex default is row
      return null;
    },
    [],
  );

  const getLayoutFromComputedStyles = useCallback(
    (parentElement: Element, contentWindow: Window): LayoutDirection => {
      try {
        const style = contentWindow.getComputedStyle(parentElement);
        const { display, flexDirection, gridAutoFlow } = style;

        if (display === "grid")
          return gridAutoFlow === "column" ? "horizontal" : "vertical";

        if (display === "flex" || display === "inline-flex")
          return flexDirection === "row" || flexDirection === "row-reverse"
            ? "horizontal"
            : "vertical";

        if (display === "inline" || display === "inline-block")
          return "horizontal";
      } catch (error) {
        console.warn("Error getting computed styles:", error);
      }
      return "vertical";
    },
    [],
  );

  const getParentLayoutDirection = useCallback((): LayoutDirection => {
    const parentNode = selectedNavTreeNode?.parent;
    if (!parentNode || !selectedElement?.storyboardId) return "vertical";

    const parentCodebaseId = parentNode.tempoElement?.codebaseId || null;

    // Return cached result if parent hasn't changed
    if (layoutCacheRef.current.parentCodebaseId === parentCodebaseId) {
      return layoutCacheRef.current.layout;
    }

    const classLayout = getLayoutFromClasses(parentNode.classList || []);
    if (classLayout) {
      layoutCacheRef.current = { parentCodebaseId, layout: classLayout };
      return classLayout;
    }

    // Fallback to computed styles (only works for application storyboards)
    const iframeDoc = getIframeDocumentForStoryboard(
      selectedElement.storyboardId,
      useIframePoolStore.getState().applicationStoryboards,
    );

    let computedLayout: LayoutDirection = "vertical";
    if (iframeDoc?.iframeElement.contentWindow) {
      const parentElement = findElementByDomUniquePath(
        iframeDoc.contentDocument,
        parentNode.domUniquePath ?? parentNode.domUniquePathForComponent,
      );
      if (parentElement) {
        computedLayout = getLayoutFromComputedStyles(
          parentElement,
          iframeDoc.iframeElement.contentWindow,
        );
      }
    }

    layoutCacheRef.current = { parentCodebaseId, layout: computedLayout };
    return computedLayout;
  }, [
    selectedNavTreeNode,
    selectedElement,
    getLayoutFromClasses,
    getLayoutFromComputedStyles,
  ]);

  const canReorderInDirection = useCallback(
    (direction: Direction): boolean => {
      if (!canElementBeReordered() || !selectedNavTreeNode?.parent)
        return false;

      const layout = getParentLayoutDirection();
      const isHorizontalKey = direction === "left" || direction === "right";

      if ((layout === "horizontal") !== isHorizontalKey) return false;

      const siblings = selectedNavTreeNode.parent.children;
      if (!siblings) return false;

      const currentIndex = siblings.findIndex((child) =>
        child.tempoElement.isEqual(selectedElement),
      );
      if (currentIndex === -1) return false;

      const isMovingUp = direction === "up" || direction === "left";
      const targetIndex = isMovingUp ? currentIndex - 1 : currentIndex + 1;

      return targetIndex >= 0 && targetIndex < siblings.length;
    },
    [
      canElementBeReordered,
      selectedNavTreeNode,
      selectedElement,
      getParentLayoutDirection,
    ],
  );

  const reorderElement = useCallback(
    async (direction: Direction): Promise<boolean> => {
      const now = Date.now();
      if (now - lastReorderTimeRef.current < REORDER_THROTTLE_MS) return false;
      if (isReorderingRef.current) return false;

      if (
        !canReorderInDirection(direction) ||
        !selectedNavTreeNode ||
        !selectedElement ||
        !activeCanvas ||
        !canvasId
      )
        return false;

      isReorderingRef.current = true;
      lastReorderTimeRef.current = now;

      try {
        const parent = selectedNavTreeNode.parent;
        if (!parent?.children) return false;

        const siblings = parent.children;
        const currentIndex = siblings.findIndex((child) =>
          child.tempoElement.isEqual(selectedElement),
        );
        if (currentIndex === -1) return false;

        const isMovingUp = direction === "up" || direction === "left";
        const targetIndex = isMovingUp ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= siblings.length) return false;

        const adjacentCodebaseId =
          siblings[targetIndex].tempoElement.codebaseId;
        const beforeCodebaseId = isMovingUp
          ? adjacentCodebaseId
          : siblings[targetIndex + 1]?.tempoElement.codebaseId;
        const afterCodebaseId = !isMovingUp
          ? adjacentCodebaseId
          : siblings[targetIndex - 1]?.tempoElement.codebaseId;

        const { newFileContents: sourceFileContents, sourceFilePath } =
          (await getComponentFileFromNavTreeNodes(
            canvasId,
            [selectedNavTreeNode],
            false,
            undefined,
          )) ?? {};

        const selectedElementHtml =
          useExtractElementStore.getState().selectedElementHTML;
        let htmlForInstantUpdate: string | undefined;
        let navTreeNodesForInstantUpdate: NavTreeNode[] | undefined;

        if (selectedElementHtml) {
          const { html, navTree, error } = processInstantUpdateData({
            storyboardId: selectedElement.storyboardId,
            canvasId,
            instantUpdateData: {
              html: selectedElementHtml,
              navTree: selectedNavTreeNode,
            },
          });

          if (!error) {
            htmlForInstantUpdate = html;
            navTreeNodesForInstantUpdate = navTree ? [navTree] : undefined;
          } else {
            console.warn("Failed to process instant update data:", error);
          }
        }

        const hashingKey = uuidv4();
        const moveChange = new AddAndRemoveJsxChange({
          addJsxChangeFields: {
            codebaseIdToAddTo: parent.tempoElement.codebaseId,
            fileContentsToSourceFrom: sourceFileContents,
            fileContentsSourceFilename: sourceFilePath ?? undefined,
            beforeCodebaseId,
            afterCodebaseId,
            codebaseIdHashingKey: hashingKey,
            htmlForInstantUpdate,
            navTreeNodesForInstantUpdate,
          },
          removeJsxChangeFields: {
            codebaseIdsToRemove: [selectedElement.codebaseId],
          },
        });

        const newElement = new TempoElement(
          updateCodebaseId(selectedElement.codebaseId, hashingKey),
          selectedElement.storyboardId,
          parent.tempoElement.uniquePath + "-" + targetIndex,
        );

        moveChange.hotReloadTs = APPLIED_BUT_NOT_CONFIRMED_BY_API_CONSTANT;
        moveChange.setSelectedElementsAfterInstantUpdate(
          newElement.getKey(),
          null,
        );

        if (selectedElement.storyboardId) {
          moveChange.storyboardsAppliedTo.add(selectedElement.storyboardId);
        }

        executeCommand(moveChange);
        return true;
      } catch (error) {
        console.error("Reorder error:", error);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        toast.error(
          `Failed to reorder${errorMessage ? `: ${errorMessage}` : ""}`,
        );
        return false;
      } finally {
        isReorderingRef.current = false;
      }
    },
    [
      canReorderInDirection,
      selectedNavTreeNode,
      selectedElement,
      activeCanvas,
      canvasId,
      executeCommand,
    ],
  );

  return { reorderElement, canReorderInDirection };
}
